<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import StepInboxSelection from './steps/StepInboxSelection.vue';
import StepAudienceSelection from './steps/StepAudienceSelection.vue';
import StepTemplateSelection from './steps/StepTemplateSelection.vue';
import StepMediaConfig from './steps/StepMediaConfig.vue';
import StepScheduling from './steps/StepScheduling.vue';
import StepReview from './steps/StepReview.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const isEditMode = computed(() => !!route.params.campaignId);
const currentStep = ref(0);
const isSubmitting = ref(false);
const isLoaded = ref(false);

const campaignData = ref({
  inbox: null,
  inboxType: null,
  audience: null,
  template: null,
  templateParams: {},
  message: '',
  mediaUrl: '',
  mediaType: null,
  templateConfig: { body: {} },
  title: '',
  description: '',
  repeatType: 'once',
  repeatConfig: {},
  dispatchTime: '',
  scheduledAt: '',
  allowResend: false,
  sendLimitTotal: null,
  sendLimitPerRun: null,
  enabled: true,
});

const loadCampaignForEdit = () => {
  const campaigns = store.getters['campaignsV2/getAllCampaigns'] || [];
  const campaign = campaigns.find(
    c => String(c.id) === String(route.params.campaignId)
  );
  if (!campaign) return;

  const inboxes = store.getters['inboxes/getInboxes'] || [];
  const inbox = inboxes.find(i => i.id === campaign.inbox?.id);

  const audiences = store.state.audiences.records || [];
  const audience = audiences.find(a => a.id === campaign.audience_id);

  campaignData.value = {
    inbox: inbox || campaign.inbox,
    inboxType: campaign.inbox?.channel_type === 'Channel::Whatsapp' ? 'whatsapp' : 'api',
    audience: audience || null,
    template: null,
    templateParams: campaign.template_params || {},
    message: campaign.message || '',
    mediaUrl: campaign.media_url || '',
    mediaType: null,
    templateConfig: campaign.template_config || { body: {} },
    title: campaign.title || '',
    description: campaign.description || '',
    repeatType: campaign.repeat_type || 'once',
    repeatConfig: campaign.repeat_config || {},
    dispatchTime: campaign.dispatch_time || '',
    scheduledAt: campaign.scheduled_at
      ? new Date(campaign.scheduled_at * 1000).toISOString().slice(0, 16)
      : '',
    allowResend: campaign.allow_resend || false,
    sendLimitTotal: campaign.send_limit_total,
    sendLimitPerRun: campaign.send_limit_per_run,
    enabled: campaign.enabled !== false,
  };
};

const hasMediaHeader = computed(() => {
  if (!campaignData.value.template) return false;
  const components = campaignData.value.template.components || [];
  return components.some(
    c => c.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format)
  );
});

const steps = computed(() => {
  const base = [
    { key: 'inbox', label: t('CAMPAIGN.V2.WIZARD.STEP_INBOX') },
    { key: 'audience', label: t('CAMPAIGN.V2.WIZARD.STEP_AUDIENCE') },
    { key: 'template', label: t('CAMPAIGN.V2.WIZARD.STEP_TEMPLATE') },
  ];

  if (hasMediaHeader.value) {
    base.push({ key: 'media', label: t('CAMPAIGN.V2.WIZARD.STEP_MEDIA') });
  }

  base.push(
    { key: 'scheduling', label: t('CAMPAIGN.V2.WIZARD.STEP_SCHEDULING') },
    { key: 'review', label: t('CAMPAIGN.V2.WIZARD.STEP_REVIEW') }
  );

  return base;
});

const currentStepKey = computed(() => steps.value[currentStep.value]?.key);
const isLastStep = computed(() => currentStep.value === steps.value.length - 1);

const goNext = () => {
  if (currentStep.value < steps.value.length - 1) {
    currentStep.value += 1;
  }
};

const goPrev = () => {
  if (currentStep.value > 0) {
    currentStep.value -= 1;
  }
};

const goBack = () => {
  router.push({ name: 'campaigns_index', params: route.params });
};

const buildPayload = () => {
  const data = campaignData.value;
  return {
    title: data.title,
    description: data.description,
    message: data.message || '',
    inbox_id: data.inbox?.id,
    audience_id: data.audience?.id,
    template_params: data.templateParams,
    template_config: data.templateConfig,
    repeat_type: data.repeatType,
    repeat_config: data.repeatConfig,
    dispatch_time: data.dispatchTime,
    scheduled_at: data.scheduledAt || new Date().toISOString(),
    allow_resend: data.allowResend,
    send_limit_total: data.sendLimitTotal || null,
    send_limit_per_run: data.sendLimitPerRun || null,
    media_url: data.mediaUrl || null,
    enabled: data.enabled,
  };
};

const submitCampaign = async () => {
  isSubmitting.value = true;
  try {
    if (isEditMode.value) {
      await store.dispatch('campaignsV2/update', {
        id: route.params.campaignId,
        ...buildPayload(),
      });
      useAlert(t('CAMPAIGN.V2.EDIT.SUCCESS'));
    } else {
      await store.dispatch('campaignsV2/create', buildPayload());
      useAlert(t('CAMPAIGN.V2.WIZARD.API.SUCCESS_MESSAGE'));
    }
    goBack();
  } catch {
    useAlert(
      isEditMode.value
        ? t('CAMPAIGN.V2.EDIT.ERROR')
        : t('CAMPAIGN.V2.WIZARD.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await store.dispatch('audiences/get');
  if (isEditMode.value) {
    if (!store.getters['campaignsV2/getAllCampaigns']?.length) {
      await store.dispatch('campaignsV2/get');
    }
    loadCampaignForEdit();
  }
  isLoaded.value = true;
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <div class="flex items-center gap-4 px-6 py-4 border-b border-n-weak">
      <button
        class="text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="goBack"
      >
        &larr; {{ t('CAMPAIGN.V2.ANALYTICS.BACK') }}
      </button>
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ isEditMode ? t('CAMPAIGN.V2.EDIT.TITLE') : t('CAMPAIGN.V2.NEW_CAMPAIGN') }}
      </h1>
    </div>

    <!-- Stepper -->
    <div class="flex items-center gap-2 px-6 py-4 overflow-x-auto border-b border-n-weak">
      <template v-for="(step, idx) in steps" :key="step.key">
        <button
          class="flex items-center gap-2 text-sm whitespace-nowrap"
          :class="idx === currentStep ? 'text-n-brand font-medium' : 'text-n-slate-11'"
          @click="currentStep = idx"
        >
          <span
            class="flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full"
            :class="
              idx < currentStep
                ? 'bg-n-brand text-white'
                : idx === currentStep
                  ? 'bg-n-brand text-white'
                  : 'bg-n-alpha-3 text-n-slate-11'
            "
          >
            {{ idx + 1 }}
          </span>
          {{ step.label }}
        </button>
        <span
          v-if="idx < steps.length - 1"
          class="text-n-slate-8"
        >
          &rarr;
        </span>
      </template>
    </div>

    <!-- Step Content -->
    <div v-if="isLoaded" class="flex-1 px-6 py-6 overflow-auto">
      <StepInboxSelection
        v-if="currentStepKey === 'inbox'"
        v-model="campaignData"
      />
      <StepAudienceSelection
        v-else-if="currentStepKey === 'audience'"
        v-model="campaignData"
      />
      <StepTemplateSelection
        v-else-if="currentStepKey === 'template'"
        v-model="campaignData"
      />
      <StepMediaConfig
        v-else-if="currentStepKey === 'media'"
        v-model="campaignData"
      />
      <StepScheduling
        v-else-if="currentStepKey === 'scheduling'"
        v-model="campaignData"
      />
      <StepReview
        v-else-if="currentStepKey === 'review'"
        :campaign-data="campaignData"
      />
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between px-6 py-4 border-t border-n-weak">
      <button
        v-if="currentStep > 0"
        class="px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
        @click="goPrev"
      >
        {{ t('CAMPAIGN.V2.WIZARD.PREVIOUS') }}
      </button>
      <div v-else />
      <button
        v-if="!isLastStep"
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90"
        @click="goNext"
      >
        {{ t('CAMPAIGN.V2.WIZARD.NEXT') }}
      </button>
      <button
        v-else
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90"
        :disabled="isSubmitting"
        @click="submitCampaign"
      >
        {{
          isSubmitting
            ? '...'
            : isEditMode
              ? t('CAMPAIGN.V2.EDIT.SAVE')
              : t('CAMPAIGN.V2.WIZARD.CREATE')
        }}
      </button>
    </div>
  </div>
</template>
